import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { sectionConfig } from '../constants/formConfig';
import { isSectionComplete } from '../utils/validation';

/**
 * Mobile Drawer Navigation Component
 * Bottom sheet navigation for mobile devices with progress tracking
 */
function MobileDrawerNav({
  computedMissingFields,
  classes,
  hasSubmitted,
  onSubmit,
  isButtonBusy,
  value,
  certificate,
  isEmailValid
}) {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    setIsOpen(false);
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 300);
  };

  const getSectionStatus = (section) => {
    // Check if section is complete (real-time positive feedback)
    const isComplete = isSectionComplete(section, value, certificate, isEmailValid);

    if (isComplete) {
      return { status: 'complete', missingCount: 0 };
    }

    // Only show error state after first submit attempt
    if (!hasSubmitted) {
      return { status: 'pending', missingCount: 0 };
    }

    const missingCount = section.fields.filter(field => computedMissingFields[field]).length;
    return { status: 'error', missingCount };
  };

  const getTotalProgress = () => {
    // Real-time progress: count completed sections immediately
    const completed = sectionConfig.filter(section =>
      isSectionComplete(section, value, certificate, isEmailValid)
    ).length;
    return { completed, total: sectionConfig.length };
  };

  const handleSubmitClick = (e) => {
    e.stopPropagation();
    onSubmit();
  };

  const { completed, total } = getTotalProgress();

  return (
    <>
      {isOpen && (
        <Box className={classes.mobileNavOverlay} onClick={() => setIsOpen(false)} />
      )}
      <Box
        className={classes.mobileNavDrawer}
        style={{ transform: isOpen ? 'translateY(0)' : 'translateY(calc(100% - 56px))' }}
      >
        <Box className={classes.mobileNavHandle} onClick={() => setIsOpen(!isOpen)}>
          <Box className={classes.mobileNavHandleIcon} />
          <Box className={classes.mobileNavProgress}>
            <Typography className={classes.mobileNavProgressText}>
              填寫進度 {completed}/{total}
            </Typography>
          </Box>
          {/* Collapsed state submit button */}
          {!isOpen && (
            <Box
              className={classes.mobileNavCollapsedSubmit}
              onClick={handleSubmitClick}
              disabled={isButtonBusy}
              component="button"
              type="button"
            >
              <Typography className={classes.mobileNavCollapsedSubmitLabel}>
                完成作答
              </Typography>
            </Box>
          )}
          <Typography className={classes.mobileNavExpandText}>
            {isOpen ? '收合 ▼' : '展開 ▲'}
          </Typography>
        </Box>
        {isOpen && (
          <Box className={classes.mobileNavContent}>
            {sectionConfig.map((section) => {
              const { status, missingCount } = getSectionStatus(section);
              const itemClass = `${classes.mobileNavItem} ${
                status === 'complete' ? classes.mobileNavItemComplete :
                status === 'error' ? classes.mobileNavItemError : ''
              }`;
              const statusClass = `${classes.mobileNavItemStatus} ${
                status === 'complete' ? classes.mobileNavItemStatusComplete :
                status === 'error' ? classes.mobileNavItemStatusError : ''
              }`;

              return (
                <Box
                  key={section.id}
                  className={itemClass}
                  onClick={() => scrollToSection(section.id)}
                >
                  <Typography className={classes.mobileNavItemLabel}>
                    {section.label}
                  </Typography>
                  <Typography className={statusClass}>
                    {status === 'complete' ? '✓ 完成' :
                     status === 'error' ? `${missingCount} 項未填` :
                     '○ 待填寫'}
                  </Typography>
                </Box>
              );
            })}
            {/* Expanded state submit button */}
            <Box
              className={classes.mobileNavExpandedSubmit}
              onClick={handleSubmitClick}
              disabled={isButtonBusy}
              component="button"
              type="button"
            >
              <Typography className={classes.mobileNavExpandedSubmitLabel}>
                完成作答 Finish
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

export default MobileDrawerNav;
